'use client'

import * as React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { XIcon } from 'lucide-react'
import { cn } from '@/lib/utils'

function Dialog({ ...props }) {
    return <DialogPrimitive.Root data-slot="dialog" {...props} />
}

function DialogTrigger({ ...props }) {
    return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
}

function DialogPortal({ ...props }) {
    return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />
}

function DialogClose({ ...props }) {
    return <DialogPrimitive.Close data-slot="dialog-close" {...props} />
}

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => {
    return (
        <DialogPrimitive.Overlay
            ref={ref}
            data-slot="dialog-overlay"
            className={cn(
                'tw:data-[state=open]:tw:animate-in tw:data-[state=closed]:tw:animate-out tw:data-[state=closed]:tw:fade-out-0',
                'tw:bg-background tw:bg-clip-padding tw:backdrop-filter tw:backdrop-blur-xs tw:bg-opacity-10 tw:backdrop-saturate-80',
                'tw:data-[state=open]:tw:fade-in-0 tw:fixed tw:inset-0 tw:z-[999]',
                className,
            )}
            {...props}
        />
    )
})
DialogOverlay.displayName = 'DialogOverlay'

function DialogContent({ className, children, ...props }) {
    return (
        <DialogPortal data-slot="dialog-portal" className="tw:!z-[200]">
            <DialogOverlay />
            <DialogPrimitive.Content
                data-slot="dialog-content"
                className={cn(
                    'tw:bg-primary tw:data-[state=open]:tw:animate-in tw:data-[state=closed]:tw:animate-out tw:data-[state=closed]:tw:fade-out-0',
                    'tw:data-[state=open]:tw:fade-in-0 tw:data-[state=closed]:tw:zoom-out-95 tw:data-[state=open]:tw:zoom-in-95',
                    'tw:fixed tw:top-[50%] tw:left-[50%] tw:z-[1000] tw:grid tw:w-sm tw:max-w-[calc(100%-2rem)] tw:translate-x-[-50%]',
                    'tw:translate-y-[-50%] tw:rounded-sm tw:border tw:shadow-lg tw:duration-200 tw:sm:tw:max-w-lg',
                    className,
                )}
                {...props}
            >
                {children}
            </DialogPrimitive.Content>
        </DialogPortal>
    )
}

function DialogHeader({ wrapperClassName, className, showCloseButton = true, ...props }) {
    return (
        <div
            className={cn('tw:border-b tw:flex tw:justify-between tw:px-6 tw:py-[22px] items-center', wrapperClassName)}
        >
            <div
                data-slot="dialog-header"
                className={cn(
                    'tw:flex tw:flex-col tw:gap-2 tw:text-center tw:sm:tw:text-left tw:flex tw:justify-between',
                    className,
                )}
                {...props}
            ></div>
            {showCloseButton && (
                <DialogPrimitive.Close
                    data-slot="dialog-close"
                    className="tw:ring-offset-background tw:focus:tw:ring-ring tw:data-[state=open]:tw:bg-accent tw:data-[state=open]:tw:text-muted-foreground tw:top-4 tw:right-4 tw:rounded-xs tw:opacity-70 tw:transition-opacity tw:hover:tw:opacity-100 tw:focus:tw:ring-2 tw:focus:tw:ring-offset-2 tw:focus:tw:outline-hidden tw:disabled:tw:pointer-events-none [&_svg]:tw:pointer-events-none [&_svg]:tw:shrink-0 [&_svg:not([class*='tw:size-'])]:tw:size-4"
                >
                    <XIcon />
                    <span className="tw:sr-only">Close</span>
                </DialogPrimitive.Close>
            )}
        </div>
    )
}

function DialogFooter({ className, ...props }) {
    return (
        <div
            data-slot="dialog-footer"
            className={cn('tw:flex tw:items-center tw:gap-2 tw:justify-end tw:py-4 tw:px-6', className)}
            {...props}
        />
    )
}

function DialogTitle({ className, ...props }) {
    return (
        <DialogPrimitive.Title
            data-slot="dialog-title"
            className={cn('tw:!text-xl tw:leading-none tw:font-semibold tw:!mb-0', className)}
            {...props}
        />
    )
}

function DialogDescription({ className, ...props }) {
    return (
        <DialogPrimitive.Description
            data-slot="dialog-description"
            className={cn('tw:text-white tw:text-sm tw:p-6 tw:border-b tw:!mb-0', className)}
            {...props}
        />
    )
}

export {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogOverlay,
    DialogPortal,
    DialogTitle,
    DialogTrigger,
}
